/* ScrollScene — AirPods Pro-style sticky scroll-scene reveals.

   Renders a tall outer wrapper (height = scenes.length * 100vh) with an
   inner sticky container. Each scene cross-fades + slight scale based on
   the viewport's scroll progress through the outer wrapper.

   Reduced-motion users get a plain stacked render — no sticky, no
   animation. Touch users still get the animation (it's scroll-driven,
   not pointer-driven; works fine on phones).

   Usage:
     <ScrollScene scenes={[
       { image: '/...', headline: '...', sub: '...' },
       { image: '/...', headline: '...' },
       { image: '/...', headline: '...' },
     ]} />
*/
import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function Scene({ scene, index, total, progress }) {
  // Each scene occupies a 1/total slice of the scroll progress.
  const start = index / total;
  const peak  = (index + 0.5) / total;
  const end   = (index + 1) / total;

  // Opacity ramps up, holds at peak, ramps down.
  const opacity = useTransform(
    progress,
    [Math.max(0, start - 0.05), start + 0.05, end - 0.05, Math.min(1, end + 0.05)],
    [0, 1, 1, 0]
  );
  // Tiny scale breath — 1 → 1.04 across the slice.
  const scale = useTransform(progress, [start, peak, end], [1, 1.04, 1]);
  // Headline slides up + fades in around the peak.
  const headlineY = useTransform(progress, [start, peak], [24, 0]);
  const headlineOpacity = useTransform(progress, [start, peak, end], [0, 1, 0]);

  return (
    <motion.div
      className="absolute inset-0 flex items-center justify-center"
      style={{ opacity }}
      data-testid={`scroll-scene-${index}`}
    >
      <motion.img
        src={scene.image}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ scale }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0E0D0C]/80 via-[#0E0D0C]/30 to-transparent" />
      <motion.div
        className="relative z-10 text-center text-[#F5EFE0] px-6 max-w-3xl"
        style={{ y: headlineY, opacity: headlineOpacity }}
      >
        <div className="text-[10px] uppercase tracking-[0.42em] mb-4 text-[#C9A961]">
          {`Scene ${String(index + 1).padStart(2, "0")} / ${String(total).padStart(2, "0")}`}
        </div>
        <h3
          className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05]"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
        >
          {scene.headline}
        </h3>
        {scene.sub && (
          <p className="mt-5 text-sm sm:text-base text-[#F5EFE0]/75 max-w-xl mx-auto leading-relaxed">
            {scene.sub}
          </p>
        )}
        {scene.cta?.href && scene.cta?.label && (
          <a
            href={scene.cta.href}
            className="inline-block mt-7 px-6 py-3 text-[11px] uppercase tracking-[0.32em] border transition-colors hover:bg-[#C9A961]/15"
            style={{ borderColor: "#C9A961", color: "#C9A961" }}
            data-testid={`scroll-scene-cta-${index}`}
          >
            {scene.cta.label}
          </a>
        )}
      </motion.div>
    </motion.div>
  );
}

export default function ScrollScene({ scenes, ...props }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

  if (!scenes || scenes.length === 0) return null;

  // Reduced-motion → static stacked render.
  if (prefersReducedMotion()) {
    return (
      <div data-testid="scroll-scene-reduced">
        {scenes.map((s, i) => (
          <section key={i} className="relative h-[60vh] overflow-hidden">
            <img src={s.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0E0D0C]/80 via-[#0E0D0C]/30 to-transparent" />
            <div className="absolute inset-0 flex items-center justify-center text-center text-[#F5EFE0] px-6">
              <h3
                className="font-serif text-4xl lg:text-5xl font-light leading-[1.05]"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
              >
                {s.headline}
              </h3>
            </div>
          </section>
        ))}
      </div>
    );
  }

  /* Configurable runway per scene. Default 100vh keeps the cross-fade
     leisurely. We lowered the Lumetier hub instance to 70vh to reduce the
     "dead-air black space" feeling that long sticky runways create on
     short content lists — the cross-fade still reads cleanly at 70vh on
     a 1440px viewport while trimming ~720px off the total page height. */
  const vhPerScene = props.vhPerScene ?? 100;
  return (
    <div
      ref={ref}
      style={{ height: `${scenes.length * vhPerScene}vh` }}
      data-testid="scroll-scene-wrapper"
    >
      <div className="sticky top-0 h-screen overflow-hidden bg-[#0E0D0C]">
        {scenes.map((scene, i) => (
          <Scene
            key={i}
            scene={scene}
            index={i}
            total={scenes.length}
            progress={scrollYProgress}
          />
        ))}
      </div>
    </div>
  );
}
